import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sprout } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { VOICES } from "./JoinVillage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PATHS = ["Anchor person", "Wisdom councillor", "Key partner", "Learner", "Not sure yet"];
const EMPTY = { name: "", email: "", path: "", voices: [], message: "" };
const field = "h-12 rounded-sm border-border bg-background focus-visible:ring-primary";

export const JoinForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (name) =>
    set("voices", form.voices.includes(name) ? form.voices.filter((x) => x !== name) : [...form.voices, name]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.path) return toast.error("Please choose how you'd like to join.");
    setSending(true);
    try {
      await axios.post(`${API}/village-joins`, form);
      toast.success("Welcome. Your request to join the village has reached us.");
      setForm(EMPTY);
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="join-village-form"
      className="mt-10 grid gap-6 rounded-sm border border-border bg-card p-8 sm:p-12 lg:grid-cols-2">
      {done && (
        <div className="rounded-sm bg-leaf/10 px-5 py-4 text-sm font-medium text-leaf lg:col-span-2" data-testid="join-success-message">
          Welcome. Your request to join the village has reached us — we'll be in touch.
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="join-name">Your name</Label>
        <Input id="join-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={field} data-testid="join-name-input" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="join-email">Email</Label>
        <Input id="join-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={field} data-testid="join-email-input" />
      </div>
      <div className="space-y-2 lg:col-span-2">
        <Label>I'd like to join as</Label>
        <Select value={form.path} onValueChange={(v) => set("path", v)}>
          <SelectTrigger className={field} data-testid="join-path-select"><SelectValue placeholder="Choose one" /></SelectTrigger>
          <SelectContent>
            {PATHS.map((p, i) => <SelectItem key={p} value={p} data-testid={`join-path-option-${i}`}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <fieldset className="space-y-3 lg:col-span-2">
        <legend className="mb-3 text-sm font-medium">The voice(s) I bring (optional)</legend>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {VOICES.map((v) => {
            const id = `voice-${v.name.split(" ")[1].toLowerCase()}`;
            return (
              <label key={v.name} htmlFor={id}
                className="flex cursor-pointer items-center gap-3 rounded-sm border border-border bg-background px-4 py-3 transition-colors duration-200 hover:border-primary">
                <Checkbox id={id} checked={form.voices.includes(v.name)} onCheckedChange={() => toggle(v.name)} data-testid={`join-${id}-checkbox`} />
                <span className="text-sm">{v.name}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
      <div className="space-y-2 lg:col-span-2">
        <Label htmlFor="join-message">What draws you to Nia? (optional)</Label>
        <Textarea id="join-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className="rounded-sm border-border bg-background" data-testid="join-message-input" />
      </div>
      <div className="lg:col-span-2">
        <button type="submit" disabled={sending} data-testid="join-submit-button"
          className="inline-flex w-full items-center justify-center gap-3 rounded-sm bg-plum px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sprout className="h-4 w-4" />}
          Ask to join the village
        </button>
      </div>
    </form>
  );
};
